import { c as _c } from "react/compiler-runtime";
import { Stack } from "@mui-wrapper";
import { DataGridPro, gridSortedRowIdsSelector, useGridApiRef } from "@mui/x-data-grid-pro";
import { useAppDispatch, useAppSelector } from "@store/store-helper";
import { bulkActionNameSelector, selectedRowIdsSelector, updatingItemsSelector } from "@store/table/table-selector";
import { setInitialFetchingItems } from "@store/table/table-slice";
import { FaroCustomCheckbox, FaroCustomRowCheckbox } from "@components/common/faro-table/faro-custom-checkbox";
import { FaroCustomRow } from "@components/common/faro-table/faro-custom-row";
import { FaroTableBulkActions } from "@components/common/faro-table/faro-table-bulk-actions";
import { FaroTableEmptyPage } from "@components/common/faro-table/faro-table-empty-page";
import { FaroTableFilter } from "@components/common/faro-table/faro-table-filter/faro-table-filter";
import { FaroTableFooter } from "@components/common/faro-table/faro-table-footer";
import { FaroTableSidePanel } from "@components/common/faro-table/faro-table-side-panel";
import { useTablePagination } from "@hooks/table/use-table-pagination";
import { useDynamicHeight } from "@hooks/use-dynamic-height";
import { TableEvents } from "@utils/track-event/track-event-list";
import { useTrackEvent } from "@utils/track-event/use-track-event";
import { useCallback, useEffect, useMemo, useState } from "react";
const DEFAULT_ROW_HEIGHT_IN_PX = 60;
const DEFAULT_HEADER_HEIGHT_IN_PX = 56;

/** Defines the default amount of items to show per page */
export const DEFAULT_TABLE_PAGE_SIZE = 30;

/**
 * A general component that receives entities and show them in a table.
 * The type of entities is generic <T>, so it can be any type of arrays of object.
 */
export function FaroTable(t0) {
  const $ = _c(87);
  const {
    entities,
    uniqueIdKey,
    getUniqueRowId,
    isLoading,
    columns,
    onRowClick,
    bulkActionButtons,
    filterChips,
    rowHeight,
    shouldHideCheckbox: t1,
    isRowSelectable,
    shouldHideHeader: t2,
    currentPage: t3,
    pageSize: t4,
    rowClassName,
    sx,
    hiddenColumns,
    onNextPage,
    onPreviousPage,
    onPageChange,
    totalCount,
    hasMoreItems,
    disabledNextPageTooltip,
    sidePanelContent,
    sidePanelContentWidth,
    isSidePanelOpened,
    tableType,
    defaultSortModel,
    paginationMode: t5,
    columnVisibilityModel,
    filterMenuTrigger,
    shouldSkipDynamicHeight,
    sortModel,
    sortingMode: t6,
    onSortModelChange,
    sortTrackingSubject,
    onColumnVisibilityModelChange,
    onColumnOrderChange,
    onSortedRowIdsChange
  } = t0;
  const shouldHideCheckbox = t1 === undefined ? false : t1;
  const shouldHideHeader = t2 === undefined ? false : t2;
  const currentPage = t3 === undefined ? 0 : t3;
  const pageSize = t4 === undefined ? DEFAULT_TABLE_PAGE_SIZE : t4;
  const paginationMode = t5 === undefined ? "client" : t5;
  const sortingMode = t6 === undefined ? "client" : t6;
  const apiRef = useGridApiRef();
  let t7;
  if ($[0] !== entities.length || $[1] !== isLoading) {
    t7 = [entities.length, isLoading];
    $[0] = entities.length;
    $[1] = isLoading;
    $[2] = t7;
  } else {
    t7 = $[2];
  }
  let t8;
  if ($[3] !== shouldSkipDynamicHeight || $[4] !== t7) {
    t8 = {
      shouldSkip: shouldSkipDynamicHeight,
      deps: t7
    };
    $[3] = shouldSkipDynamicHeight;
    $[4] = t7;
    $[5] = t8;
  } else {
    t8 = $[5];
  }
  const {
    ref: tableRef,
    height: dynamicHeight
  } = useDynamicHeight(t8);
  const selectedRowIds = useAppSelector(selectedRowIdsSelector);
  const updatingItems = useAppSelector(updatingItemsSelector);
  const bulkActionName = useAppSelector(bulkActionNameSelector);
  const {
    trackEvent
  } = useTrackEvent();
  const dispatch = useAppDispatch();
  const [pageIndex, setPageIndex] = useState(currentPage);
  let t10;
  let t9;
  if ($[6] !== entities.length || $[7] !== pageIndex || $[8] !== pageSize) {
    t9 = () => {
      const currentPageStart = pageIndex * pageSize;
      if (pageIndex > 0 && currentPageStart >= entities.length) {
        setPageIndex(0);
      }
    };
    t10 = [entities.length, pageIndex, pageSize];
    $[6] = entities.length;
    $[7] = pageIndex;
    $[8] = pageSize;
    $[9] = t10;
    $[10] = t9;
  } else {
    t10 = $[9];
    t9 = $[10];
  }
  useEffect(t9, t10);
  let t11;
  if ($[11] !== onSortModelChange || $[12] !== sortModel || $[13] !== sortTrackingSubject || $[14] !== trackEvent) {
    t11 = newModel => {
      if (newModel.length === 0 && sortModel && sortModel.length > 0) {
        return;
      }
      const currentSortItem = newModel[0];
      if (sortTrackingSubject && currentSortItem.field && currentSortItem.sort) {
        trackEvent({
          name: TableEvents.sortItems,
          props: {
            subject: sortTrackingSubject,
            sortBy: currentSortItem.field,
            sortOrder: currentSortItem.sort,
            viewMode: "list"
          }
        });
      }
      onSortModelChange?.(newModel);
    };
    $[11] = onSortModelChange;
    $[12] = sortModel;
    $[13] = sortTrackingSubject;
    $[14] = trackEvent;
    $[15] = t11;
  } else {
    t11 = $[15];
  }
  const handleSortModelChange = t11;
  let t12;
  if ($[16] !== onNextPage || $[17] !== onPageChange || $[18] !== onPreviousPage || $[19] !== tableType || $[20] !== trackEvent) {
    t12 = async (newPage, direction) => {
      trackEvent({
        name: TableEvents.changePage,
        props: {
          page: newPage,
          pageToChange: direction,
          tableType
        }
      });
      if (direction === "next") {
        await onNextPage?.();
      } else {
        if (direction === "previous") {
          onPreviousPage?.();
        }
      }
      onPageChange?.(newPage);
    };
    $[16] = onNextPage;
    $[17] = onPageChange;
    $[18] = onPreviousPage;
    $[19] = tableType;
    $[20] = trackEvent;
    $[21] = t12;
  } else {
    t12 = $[21];
  }
  const handlePageChange = t12;
  let serverSidePaginationProps;
  if (paginationMode === "server") {
    const t13 = totalCount ?? null;
    let t14;
    if ($[22] !== hasMoreItems || $[23] !== onNextPage || $[24] !== t13) {
      t14 = {
        totalCount: t13,
        hasMoreItems,
        fetchNextPage: onNextPage
      };
      $[22] = hasMoreItems;
      $[23] = onNextPage;
      $[24] = t13;
      $[25] = t14;
    } else {
      t14 = $[25];
    }
    serverSidePaginationProps = t14;
  }
  let t13;
  if ($[26] !== entities || $[27] !== handlePageChange || $[28] !== pageIndex || $[29] !== pageSize || $[30] !== paginationMode || $[31] !== serverSidePaginationProps) {
    t13 = {
      mode: paginationMode,
      entities,
      pageIndex,
      setPageIndex,
      pageSize,
      serverSidePaginationProps,
      onPageChange: handlePageChange
    };
    $[26] = entities;
    $[27] = handlePageChange;
    $[28] = pageIndex;
    $[29] = pageSize;
    $[30] = paginationMode;
    $[31] = serverSidePaginationProps;
    $[32] = t13;
  } else {
    t13 = $[32];
  }
  const pagination = useTablePagination(t13);
  const shouldShowEmptyPage = entities.length === 0 && !isLoading && bulkActionName === null;
  let t14;
  let t15;
  if ($[33] !== apiRef || $[34] !== onSortedRowIdsChange || $[35] !== shouldShowEmptyPage) {
    t14 = () => {
      const api = apiRef.current;
      if (!onSortedRowIdsChange || !api || shouldShowEmptyPage) {
        return;
      }
      const notify = function notify() {
        onSortedRowIdsChange?.(gridSortedRowIdsSelector(apiRef).map(String));
      };
      notify();
      return api.subscribeEvent("sortedRowsSet", notify);
    };
    t15 = [apiRef, onSortedRowIdsChange, shouldShowEmptyPage];
    $[33] = apiRef;
    $[34] = onSortedRowIdsChange;
    $[35] = shouldShowEmptyPage;
    $[36] = t14;
    $[37] = t15;
  } else {
    t14 = $[36];
    t15 = $[37];
  }
  useEffect(t14, t15);
  let allRowIds;
  let mutableRows;
  if ($[38] !== entities || $[39] !== getUniqueRowId || $[40] !== uniqueIdKey) {
    mutableRows = [];
    allRowIds = [];
    for (const [index, entity] of entities.entries()) {
      const id = getUniqueRowId ? getUniqueRowId(entity, index) : String(entity[uniqueIdKey]);
      mutableRows.push({
        id,
        entity
      });
      allRowIds.push(id);
    }
    $[38] = entities;
    $[39] = getUniqueRowId;
    $[40] = uniqueIdKey;
    $[41] = allRowIds;
    $[42] = mutableRows;
  } else {
    allRowIds = $[41];
    mutableRows = $[42];
  }
  const rows = mutableRows;
  let t16;
  if ($[43] === Symbol.for("react.memo_cache_sentinel")) {
    t16 = {
      zIndex: 2
    };
    $[43] = t16;
  } else {
    t16 = $[43];
  }
  let t17;
  if ($[44] !== dynamicHeight || $[45] !== sx) {
    t17 = {
      height: dynamicHeight,
      "& .MuiDataGrid-columnHeaders": t16,
      ...sx
    };
    $[44] = dynamicHeight;
    $[45] = sx;
    $[46] = t17;
  } else {
    t17 = $[46];
  }
  const mergedSx = t17;
  let t18;
  if ($[47] !== filterChips) {
    t18 = filterChips && <FaroTableFilter filterChips={filterChips} />;
    $[47] = filterChips;
    $[48] = t18;
  } else {
    t18 = $[48];
  }
  let t19;
  if ($[49] !== allRowIds || $[50] !== apiRef || $[51] !== bulkActionButtons || $[52] !== bulkActionName || $[53] !== columnVisibilityModel || $[54] !== columns || $[55] !== defaultSortModel || $[56] !== disabledNextPageTooltip || $[57] !== dispatch || $[58] !== filterMenuTrigger || $[59] !== handleSortModelChange || $[60] !== hiddenColumns || $[61] !== isLoading || $[62] !== isRowSelectable || $[63] !== isSidePanelOpened || $[64] !== mergedSx || $[65] !== onColumnOrderChange || $[66] !== onColumnVisibilityModelChange || $[67] !== onRowClick || $[68] !== pageSize || $[69] !== pagination || $[70] !== rowClassName || $[71] !== rowHeight || $[72] !== rows || $[73] !== selectedRowIds || $[74] !== shouldHideCheckbox || $[75] !== shouldHideHeader || $[76] !== shouldShowEmptyPage || $[77] !== sidePanelContent || $[78] !== sidePanelContentWidth || $[79] !== sortModel || $[80] !== sortingMode || $[81] !== tableRef || $[82] !== updatingItems) {
    t19 = shouldShowEmptyPage ? <FaroTableEmptyPage /> : <>{bulkActionButtons && <FaroTableBulkActions bulkActionsButtons={bulkActionButtons} numberOfSelectedRows={updatingItems.length} isLoading={isLoading} allRowIds={allRowIds} />}<Stack ref={tableRef} sx={{
        flexDirection: "row",
        gap: sidePanelContent && isSidePanelOpened ? "20px" : "0"
      }}>{filterMenuTrigger && filterMenuTrigger}<DataGridPro apiRef={apiRef} rows={rows} sx={mergedSx} columns={columns.map(_temp)} sortingOrder={["asc", "desc"]} slots={{
          row: FaroCustomRow,
          baseCheckbox: FaroCustomCheckbox,
          rowCheckbox: FaroCustomRowCheckbox,
          footer: FaroTableFooter
        }} slotProps={{
          footer: {
            paginationModel: pagination.paginationModel,
            totalItems: pagination.footerData.rowCount,
            changePage: pagination.handlePageChange,
            paginationProps: pagination.footerData,
            shouldDisableNextPage: pagination.shouldDisableNextPage,
            disabledNextPageTooltip
          }
        }} initialState={{
          columns: {
            columnVisibilityModel: hiddenColumns?.reduce(_temp2, {})
          },
          sorting: {
            sortModel: defaultSortModel
          }
        }} disableColumnFilter={true} disableColumnMenu={true} disableRowSelectionOnClick={true} checkboxSelection={!shouldHideCheckbox} disableRowSelectionExcludeModel={true} keepNonExistentRowsSelected={bulkActionName !== null} isRowSelectable={isRowSelectable} pagination={true} pageSizeOptions={[pageSize]} paginationModel={pagination.paginationModel} hideFooter={false} loading={isLoading} columnHeaderHeight={shouldHideHeader ? 0 : DEFAULT_HEADER_HEIGHT_IN_PX} rowHeight={rowHeight ?? DEFAULT_ROW_HEIGHT_IN_PX} onRowSelectionModelChange={newSelectedRowIds => {
          dispatch(setInitialFetchingItems([...newSelectedRowIds.ids]));
        }} onRowClick={row => onRowClick?.(row.row.entity)} rowSelectionModel={{
          type: "include",
          ids: new Set(selectedRowIds)
        }} getRowClassName={rowClassName} sortModel={sortModel} sortingMode={sortingMode} onSortModelChange={handleSortModelChange} columnVisibilityModel={columnVisibilityModel} onColumnVisibilityModelChange={onColumnVisibilityModelChange} onColumnOrderChange={onColumnOrderChange} /><FaroTableSidePanel isSidePanelOpened={isSidePanelOpened} sidePanelContentWidth={sidePanelContentWidth} sidePanelContent={sidePanelContent} /></Stack></>;
    $[49] = allRowIds;
    $[50] = apiRef;
    $[51] = bulkActionButtons;
    $[52] = bulkActionName;
    $[53] = columnVisibilityModel;
    $[54] = columns;
    $[55] = defaultSortModel;
    $[56] = disabledNextPageTooltip;
    $[57] = dispatch;
    $[58] = filterMenuTrigger;
    $[59] = handleSortModelChange;
    $[60] = hiddenColumns;
    $[61] = isLoading;
    $[62] = isRowSelectable;
    $[63] = isSidePanelOpened;
    $[64] = mergedSx;
    $[65] = onColumnOrderChange;
    $[66] = onColumnVisibilityModelChange;
    $[67] = onRowClick;
    $[68] = pageSize;
    $[69] = pagination;
    $[70] = rowClassName;
    $[71] = rowHeight;
    $[72] = rows;
    $[73] = selectedRowIds;
    $[74] = shouldHideCheckbox;
    $[75] = shouldHideHeader;
    $[76] = shouldShowEmptyPage;
    $[77] = sidePanelContent;
    $[78] = sidePanelContentWidth;
    $[79] = sortModel;
    $[80] = sortingMode;
    $[81] = tableRef;
    $[82] = updatingItems;
    $[83] = t19;
  } else {
    t19 = $[83];
  }
  let t20;
  if ($[84] !== t18 || $[85] !== t19) {
    t20 = <>{t18}{t19}</>;
    $[84] = t18;
    $[85] = t19;
    $[86] = t20;
  } else {
    t20 = $[86];
  }
  return t20;
}
function _temp2(acc, key) {
  return {
    ...acc,
    [key]: false
  };
}
function _temp(column) {
  return {
    ...column,
    sortable: column.sortable ?? false,
    display: column.display ?? "flex"
  };
}